import React from 'react';
import { Mail, Clock, MessageCircle, Phone } from 'lucide-react';
import { ContactButtons } from './ContactButtons';
import { useLanguage, Language } from '../contexts/LanguageContext';
type ContactStrings = {
  heading: string;
  heading2: string;
  subtitle: string;
  subtitle2: string;
  subtitle3: string;
  callTitle: string;
  callBody: string;
  emailTitle: string;
  emailBody: string;
  multiTitle: string;
  multiBody: string;
  hoursTitle: string;
  hoursBody1: string;
  hoursBody2: string;
  formTitle: string;
  formSubtitle: string;
  firstName: string;
  lastName: string;
  email: string;
  language: string;
  message: string;
  firstNamePh: string;
  lastNamePh: string;
  emailPh: string;
  messagePh: string;
  sendBtn: string;
  privacyNote: string;
};
// EN/ES finalized. HT/VI = best effort, pending native review.
const T: Record<Language, ContactStrings> = {
  en: {
    heading: 'Need help?',
    heading2: "We're here for you, any time.",
    subtitle: 'Reach out in your preferred language, day or night.',
    subtitle2: 'The fastest way is to call or message us on WhatsApp.',
    subtitle3: 'Asking which form you need is always free.',
    callTitle: 'Call or message us — 24/7',
    callBody: 'Call any hour, day or night — our multilingual virtual assistant always answers and will help you in your language. Asking which form you need is free.',
    emailTitle: 'Email Support',
    emailBody: 'We respond within 24 hours.',
    multiTitle: 'Multilingual Help',
    multiBody: "Reach us in English, Spanish, Haitian Creole, or Vietnamese — we'll help you in the same language.",
    hoursTitle: 'Open 24/7',
    hoursBody1: 'Fill out your form online anytime — day or night, any day of the week.',
    hoursBody2: 'Our virtual assistant answers calls around the clock to guide you in your language.',
    formTitle: 'Send us a message',
    formSubtitle: "We'll get back to you within one business day.",
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email Address',
    language: 'Preferred Language',
    message: 'Message',
    firstNamePh: 'John',
    lastNamePh: 'Doe',
    emailPh: 'john@example.com',
    messagePh: 'How can we help you?',
    sendBtn: 'Send Message',
    privacyNote: 'Please do not share sensitive document numbers (passport, Social Security) by email.'
  },
  es: {
    heading: '¿Necesita ayuda?',
    heading2: 'Estamos para usted a cualquier hora',
    subtitle: 'Comuníquese en el idioma que prefiera, de día o de noche.',
    subtitle2: 'La forma más rápida es llamarnos o escribirnos por WhatsApp.',
    subtitle3: 'Preguntar cuál formulario necesita siempre es gratis.',
    callTitle: 'Llámenos o escríbanos — 24/7',
    callBody: 'Llame a cualquier hora, de día o de noche — nuestro asistente virtual multilingüe siempre contesta y le ayuda en su idioma. Preguntar cuál formulario necesita es gratis.',
    emailTitle: 'Soporte por correo',
    emailBody: 'Respondemos en menos de 24 horas.',
    multiTitle: 'Ayuda multilingüe',
    multiBody: 'Comuníquese en inglés, español, creole haitiano o vietnamita — le ayudamos en el mismo idioma.',
    hoursTitle: 'Abierto 24/7',
    hoursBody1: 'Llene su formulario en línea a cualquier hora — de día o de noche, cualquier día de la semana.',
    hoursBody2: 'Nuestro asistente virtual contesta llamadas a toda hora para guiarle en su idioma.',
    formTitle: 'Envíenos un mensaje',
    formSubtitle: 'Le responderemos en un día hábil.',
    firstName: 'Nombre',
    lastName: 'Apellido',
    email: 'Correo electrónico',
    language: 'Idioma preferido',
    message: 'Mensaje',
    firstNamePh: 'Juan',
    lastNamePh: 'Pérez',
    emailPh: 'juan@ejemplo.com',
    messagePh: '¿Cómo podemos ayudarle?',
    sendBtn: 'Enviar mensaje',
    privacyNote: 'Por favor no comparta números de documentos delicados (pasaporte, Seguro Social) por correo.'
  },
  ht: {
    heading: 'Ou bezwen èd?',
    heading2: 'Nou la pou ou nenpòt lè',
    subtitle: 'Kontakte nou nan lang ou pi pito, lajounen kou lannwit.',
    subtitle2: 'Fason ki pi rapid se rele oswa voye mesaj ban nou sou WhatsApp.',
    subtitle3: 'Mande ki fòm ou bezwen toujou gratis.',
    callTitle: 'Rele oswa voye mesaj ban nou — 24/7',
    callBody: 'Rele nenpòt lè, lajounen kou lannwit — asistan vityèl miltileng nou an toujou reponn epi l ap ede ou nan lang ou. Mande ki fòm ou bezwen gratis.',
    emailTitle: 'Sipò pa imèl',
    emailBody: 'Nou reponn nan mwens pase 24 èdtan.',
    multiTitle: 'Èd nan plizyè lang',
    multiBody: 'Kontakte nou an anglè, panyòl, kreyòl ayisyen, oswa vyetnamyen — n ap ede ou nan menm lang lan.',
    hoursTitle: 'Louvri 24/7',
    hoursBody1: 'Ranpli fòm ou sou entènèt nenpòt lè — lajounen kou lannwit, nenpòt jou nan semèn nan.',
    hoursBody2: 'Asistan vityèl nou an reponn apèl yo tout lè pou gide ou nan lang ou.',
    formTitle: 'Voye nou yon mesaj',
    formSubtitle: 'N ap reponn ou nan yon jou ouvrab.',
    firstName: 'Prenon',
    lastName: 'Siyati',
    email: 'Adrès imèl',
    language: 'Lang ou pi pito',
    message: 'Mesaj',
    firstNamePh: 'Jan',
    lastNamePh: 'Pyè',
    emailPh: 'jan@egzanp.com',
    messagePh: 'Kijan nou ka ede ou?',
    sendBtn: 'Voye mesaj la',
    privacyNote: 'Tanpri pa pataje nimewo dokiman sansib (paspò, Sekirite Sosyal) pa imèl.'
  },
  vi: {
    heading: 'Cần trợ giúp?',
    heading2: 'Chúng tôi luôn sẵn sàng, bất cứ lúc nào',
    subtitle: 'Hãy liên hệ bằng ngôn ngữ quý vị muốn, ngày hay đêm.',
    subtitle2: 'Cách nhanh nhất là gọi hoặc nhắn tin cho chúng tôi qua WhatsApp.',
    subtitle3: 'Hỏi quý vị cần mẫu đơn nào luôn miễn phí.',
    callTitle: 'Gọi hoặc nhắn tin cho chúng tôi — 24/7',
    callBody: 'Gọi bất cứ giờ nào, ngày hay đêm — trợ lý ảo đa ngôn ngữ của chúng tôi luôn trả lời và sẽ giúp quý vị bằng ngôn ngữ của mình. Hỏi quý vị cần mẫu đơn nào là miễn phí.',
    emailTitle: 'Hỗ trợ qua email',
    emailBody: 'Chúng tôi phản hồi trong vòng 24 giờ.',
    multiTitle: 'Hỗ trợ đa ngôn ngữ',
    multiBody: 'Liên hệ với chúng tôi bằng tiếng Anh, Tây Ban Nha, Creole Haiti hoặc tiếng Việt — chúng tôi sẽ giúp quý vị bằng chính ngôn ngữ đó.',
    hoursTitle: 'Mở cửa 24/7',
    hoursBody1: 'Điền mẫu đơn của quý vị trực tuyến bất cứ lúc nào — ngày hay đêm, bất kỳ ngày nào trong tuần.',
    hoursBody2: 'Trợ lý ảo của chúng tôi trả lời cuộc gọi suốt ngày đêm để hướng dẫn quý vị bằng ngôn ngữ của mình.',
    formTitle: 'Gửi tin nhắn cho chúng tôi',
    formSubtitle: 'Chúng tôi sẽ phản hồi trong vòng một ngày làm việc.',
    firstName: 'Tên',
    lastName: 'Họ',
    email: 'Địa chỉ email',
    language: 'Ngôn ngữ ưu tiên',
    message: 'Tin nhắn',
    firstNamePh: 'Văn',
    lastNamePh: 'Nguyễn',
    emailPh: 'vannguyen@vidu.com',
    messagePh: 'Chúng tôi có thể giúp gì cho quý vị?',
    sendBtn: 'Gửi tin nhắn',
    privacyNote: 'Vui lòng không chia sẻ số giấy tờ nhạy cảm (hộ chiếu, An sinh Xã hội) qua email.'
  }
};
export function ContactSection() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  return <section className="py-24 bg-white relative z-10 border-t border-brand-border" data-id="element-67" data-mp="eai79t2o0xa8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-68" data-mp="9fmhz0otco0t">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-69" data-mp="5ess41rt8z5a">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-textPrimary mb-4" data-id="element-70" data-mp="gr9tj27l3uv">
            {t.heading}
            <span className="block" data-id="element-71" data-mp="3rnby4v0vpxo7">{t.heading2}</span>
          </h2>
          <p className="text-lg text-brand-textSecondary" data-id="element-72" data-mp="3rp50hy4glvqe">
            {t.subtitle}
            <span className="block" data-id="element-73" data-mp="3mo8zbsbhsnoo">{t.subtitle2}</span>
            <span className="block" data-id="element-74" data-mp="3in5l1a93ezpk">{t.subtitle3}</span>
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start" data-id="element-75" data-mp="1kwcd9orp7vfn">
          {/* Contact details */}
          <div className="lg:col-span-2 space-y-6" data-id="element-76" data-mp="1guhh0yomk88q">
            {/* Call / WhatsApp — primary, fastest channel */}
            <div className="bg-white rounded-2xl p-6 border-2 border-brand-accent shadow-premium" data-id="element-77" data-mp="204tq0hwvru0z">
              <div className="flex items-center gap-2 mb-1" data-id="element-78" data-mp="244aylk7lt7ad">
                <Phone className="w-5 h-5 text-brand-accent" aria-hidden="true" data-id="element-79" data-mp="1qdz2zbbt8qeh" />
                <h3 className="text-lg font-semibold text-brand-textPrimary" data-id="element-80" data-mp="1qc4tct5zokpp">
                  {t.callTitle}
                </h3>
              </div>
              <p className="text-brand-textSecondary text-sm mb-4" data-id="element-81" data-mp="1geqdaunz9xxx">
                {t.callBody}
              </p>
              <ContactButtons size="lg" showNumber className="flex flex-col items-stretch" data-id="element-82" data-mp="1lfnt50limfw9" />
            </div>

            <div className="bg-brand-bg/40 rounded-2xl p-6 border border-brand-border" data-id="element-83" data-mp="1cdek6f5waukz">
              <div className="flex items-start gap-4" data-id="element-84" data-mp="31sl1e1f7rz2j">
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0 border border-brand-border" data-id="element-85" data-mp="31tw7i7w4tiwt">
                  <Mail className="w-5 h-5 text-brand-accent" data-id="element-86" data-mp="3gvmh2t419xu9" />
                </div>
                <div data-id="element-87" data-mp="3bx1i0x0ipk7e">
                  <h3 className="text-lg font-semibold text-brand-textPrimary mb-1" data-id="element-88" data-mp="3bxudv2xxupar">
                    {t.emailTitle}
                  </h3>
                  <p className="text-brand-textSecondary text-sm mb-2" data-id="element-89" data-mp="3ppr411g1qp4i">
                    {t.emailBody}
                  </p>
                  <a href="mailto:info@noorpassportservices.com" className="text-brand-secondaryLink hover:text-brand-textPrimary font-medium transition-colors break-all underline underline-offset-2" data-id="element-90" data-mp="3h9d29fgn2iq">
                    info@noorpassportservices.com
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-brand-bg/40 rounded-2xl p-6 border border-brand-border" data-id="element-91" data-mp="8ehycivwnmo7">
              <div className="flex items-start gap-4" data-id="element-92" data-mp="2ebdk27oeyttr">
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0 border border-brand-border" data-id="element-93" data-mp="2a8qyupm9075j">
                  <MessageCircle className="w-5 h-5 text-brand-accent" data-id="element-94" data-mp="yb89up8k4bk0" />
                </div>
                <div data-id="element-95" data-mp="uag445yw8osh">
                  <h3 className="text-lg font-semibold text-brand-textPrimary mb-1" data-id="element-96" data-mp="pcmx5aprnqr5">
                    {t.multiTitle}
                  </h3>
                  <p className="text-brand-textSecondary text-sm" data-id="element-97" data-mp="keldankinnem">
                    {t.multiBody}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-brand-bg/40 rounded-2xl p-6 border border-brand-border" data-id="element-98" data-mp="1d1peehghnhl4">
              <div className="flex items-start gap-4" data-id="element-99" data-mp="18b8v4gpzl1qu">
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0 border border-brand-border" data-id="element-100" data-mp="189o594ema6zb">
                  <Clock className="w-5 h-5 text-brand-accent" data-id="element-101" data-mp="20rjpoul5sy39" />
                </div>
                <div data-id="element-102" data-mp="1w13bh89il8fu">
                  <h3 className="text-lg font-semibold text-brand-textPrimary mb-1" data-id="element-103" data-mp="1w0l7tqkivrxw">
                    {t.hoursTitle}
                  </h3>
                  <p className="text-brand-textSecondary text-sm" data-id="element-104" data-mp="23e2ilzdy7z6d">
                    {t.hoursBody1}
                  </p>
                  <p className="text-brand-textSecondary text-sm mt-1" data-id="element-105" data-mp="28d5mhgi6zzcr">
                    {t.hoursBody2}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3 bg-brand-bg/40 rounded-3xl p-8 md:p-10 border border-brand-border shadow-sm" data-id="element-106" data-mp="2mbn7h6gpqxqz">
            <h3 className="text-2xl font-bold text-brand-textPrimary mb-2" data-id="element-107" data-mp="2r957g7cr62mu">
              {t.formTitle}
            </h3>
            <p className="text-brand-textSecondary mb-8" data-id="element-108" data-mp="2w1f4068di0y3">{t.formSubtitle}</p>

            <form className="space-y-5" data-id="element-109" data-mp="30yyjtakpyls5">
              <div className="grid sm:grid-cols-2 gap-5" data-id="element-110" data-mp="3122rihxe7znk">
                <div data-id="element-111" data-mp="3604c57agw715">
                  <label htmlFor="firstName" className="block text-base font-medium text-brand-textPrimary mb-2" data-id="element-112" data-mp="360xmsjcjrgok">
                    {t.firstName}
                  </label>
                  <input type="text" id="firstName" className="w-full px-4 py-3.5 text-base rounded-xl border border-brand-border focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all bg-white min-h-[48px]" placeholder={t.firstNamePh} data-id="element-113" data-mp="15wtrvhecbm1j" />
                </div>
                <div data-id="element-114" data-mp="1to8tf7yalzbq">
                  <label htmlFor="lastName" className="block text-base font-medium text-brand-textPrimary mb-2" data-id="element-115" data-mp="1onulrr0lsre8">
                    {t.lastName}
                  </label>
                  <input type="text" id="lastName" className="w-full px-4 py-3.5 text-base rounded-xl border border-brand-border focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all bg-white min-h-[48px]" placeholder={t.lastNamePh} data-id="element-116" data-mp="1ymtwdf4xix3v" />
                </div>
              </div>

              <div data-id="element-117" data-mp="3ua0skqsy4kck">
                <label htmlFor="email" className="block text-base font-medium text-brand-textPrimary mb-2" data-id="element-118" data-mp="3ucmvgd4w6bog">
                  {t.email}
                </label>
                <input type="email" id="email" className="w-full px-4 py-3.5 text-base rounded-xl border border-brand-border focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all bg-white min-h-[48px]" placeholder={t.emailPh} data-id="element-119" data-mp="36ky0ep0or93t" />
              </div>

              <div data-id="element-120" data-mp="32jmht3g8bo54">
                <label htmlFor="language" className="block text-base font-medium text-brand-textPrimary mb-2" data-id="element-121" data-mp="32l7byrymt52g">
                  {t.language}
                </label>
                <select id="language" defaultValue={language} className="w-full px-4 py-3.5 text-base rounded-xl border border-brand-border focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all bg-white min-h-[48px] appearance-none bg-no-repeat bg-right pr-10" style={{
                backgroundImage: "url(\"data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath fill='%235F6B7A' d='M5.5 7.5L10 12l4.5-4.5'/%3E%3C/svg%3E\")",
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1.25rem'
              }} data-id="element-122" data-mp="2wuw0ab8gwe0m">
                  <option value="en" data-id="element-123" data-mp="1z5auvxj5ekix">English</option>
                  <option value="es" data-id="element-124" data-mp="1v621wuvxmqof">Español</option>
                  <option value="ht" data-id="element-125" data-mp="1v7li6lhl91q7">Kreyòl Ayisyen</option>
                  <option value="vi" data-id="element-126" data-mp="1q80j8b2f4tdh">Tiếng Việt</option>
                </select>
              </div>

              <div data-id="element-127" data-mp="5nvcewe9ox40">
                <label htmlFor="message" className="block text-base font-medium text-brand-textPrimary mb-2" data-id="element-128" data-mp="5odjyp6dqxr8">
                  {t.message}
                </label>
                <textarea id="message" rows={5} className="w-full px-4 py-3.5 text-base rounded-xl border border-brand-border focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all bg-white resize-none" placeholder={t.messagePh} data-id="element-129" data-mp="enigxr2amj1a"></textarea>
              </div>

              <button type="button" className="w-full bg-brand-accent hover:bg-brand-accentHover text-brand-textPrimary px-6 py-4 rounded-full font-semibold text-lg transition-colors shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2 min-h-[52px]" data-id="element-130" data-mp="3s2wto8ketord">
                {t.sendBtn}
              </button>

              <p className="text-xs text-brand-textSecondary text-center pt-2" data-id="element-131" data-mp="bic1v2mjvi5s">
                {t.privacyNote}
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>;
}